<script module lang="ts">
	import type { ISbStoryData, SbBlokData } from '@storyblok/js';

	export interface ResultProps extends SbBlokData {
		component: 'quiz_coupon_code_result';
		title: string;
		results: Array<
			ISbStoryData<{ quote: string; customer_name: string; tracking_label: string }>['content']
		>;
		cta: Array<ISbStoryData<ButtonV3Props>['content']>;
		coupon_identifier: string;
		coupon_info_text?: string;
	}
</script>

<script lang="ts">
	import Blok from '$lib/storyblok/blok.svelte';
	import { editable } from '$lib/storyblok/editable';
	import type { ButtonV3Props } from '../buttonV3.svelte';
	import { goto } from '$app/navigation';
	import { getSbUrl } from '$lib/utils/storyblok/url';
	import StarRating from '$lib/review/starRating.svelte';
	import { checkoutSession } from '$lib/state/checkoutSession.svelte';
	import { getCouponValue } from '$lib/utils/couponCode';
	import { formatMoney, inDollars } from '$lib/utils/money';
	import { slide } from 'svelte/transition';

	interface Props {
		content: ISbStoryData<ResultProps>['content'];
		resultIndex: number;
		onclose?: () => void;
	}

	let { content, resultIndex, onclose }: Props = $props();

	const couponValue = $derived(
		checkoutSession.data.product?.promotionCode?.coupon
			? inDollars(getCouponValue(checkoutSession.data.product?.promotionCode?.coupon))
			: 0
	);
</script>

<section
	class="flex h-full w-full flex-col justify-center bg-white text-center"
	use:editable={content}
>
	<input type="hidden" name="couponIdentifier" value={content.coupon_identifier} />
	<input type="hidden" name="couponInfoText" value={content.coupon_info_text ?? ''} />

	{#if couponValue > 0}
		<span in:slide class="mb-2 text-[14px] font-semibold uppercase tracking-[1.12px] text-green-3"
			>{formatMoney(couponValue)} off</span
		>
	{/if}

	<h1 class="mb-6 text-balance font-rhymes text-[32px]/[130%] text-brown-1">{content.title}</h1>

	{#each content.cta as block}
		<Blok
			content={block}
			onclick={() => {
				goto(getSbUrl(block.link));
				onclose?.();
			}}
		/>
	{/each}

	{#if content.results[resultIndex]}
		<div
			class="mx-auto mt-15 flex flex-col items-center rounded-lg bg-brown-6 px-9 py-5 lg:w-[360px]"
		>
			<StarRating theme="brown-gradient" score={5} />

			<p class="my-2 text-[14px]/[160%] text-brown-4">Over 1,300 5-Star-Reviews</p>

			<Blok content={content.results[resultIndex]} />
		</div>
	{/if}
</section>
